import { ts } from '@servicenow/sdk-build-core'
import type { Compiler } from '@servicenow/sdk-build-core'

/**
 * Resolves all valid field names for a ServiceNow table.
 *
 * ## How it works
 *
 * The SDK ships TypeScript definitions for every platform table. For example,
 * `Data<'change_request'>` resolves to an object type whose keys are the
 * table's field names (including inherited fields from parent tables like `task`).
 *
 * To read those field names at build time, we create a tiny temporary source
 * file that declares `type T = Data<'table_name'>`, ask the TypeScript type
 * checker to resolve it, and then read back the property names. The temporary
 * file is removed immediately after — this is the same pattern used by
 * `Compiler.getTableColumnTypes()` elsewhere in the codebase.
 *
 * ## Why a temporary file?
 *
 * ts-morph (the TypeScript wrapper used by the build system) can only resolve
 * a generic type instantiation like `Data<'change_request'>` when there is an
 * actual source node referencing it. Creating a one-line temp file is the
 * standard ts-morph technique for this — the alternative would be manually
 * reimplementing the recursive type algebra that TypeScript's checker already
 * handles for us.
 *
 * Results are cached so each table is resolved at most once per build.
 */

// Module-level cache: persists across calls within a single build process so
// the same table is never resolved twice.
const tableFieldNamesCache = new Map<string, Set<string>>()

/**
 * Returns the set of all field names for a given ServiceNow table, or
 * `undefined` if the table type cannot be resolved (e.g. unknown table name).
 *
 * @param compiler  - The ts-morph Project (Compiler) that has table type
 *                    definitions loaded. Passed through from the plugin context.
 * @param rootDir   - Project root directory, used to place the temporary file
 *                    in a known location (`src/` subdirectory).
 * @param tableName - The ServiceNow table name (e.g. 'change_request').
 */
export function resolveTableFieldNames(
    compiler: Compiler,
    rootDir: string,
    tableName: string
): Set<string> | undefined {
    // Return cached result when available.
    if (tableFieldNamesCache.has(tableName)) {
        return tableFieldNamesCache.get(tableName)
    }

    // 1. Create a temporary source file that references Data<'table_name'>.
    //    This gives the TypeScript type checker a concrete node to resolve.
    const tempFilePath = `${rootDir}/src/$$TEMP_FIELD_NAMES_RESOLVER$$.ts`
    const typeDeclaration = `type T = import('@servicenow/sdk-core/db').Data<'${tableName}'>`

    const tempSource = compiler.createSourceFile(tempFilePath, typeDeclaration, {
        overwrite: true,
        scriptKind: ts.ScriptKind.TS,
    })

    try {
        // 2. Ask ts-morph to resolve the type alias we just created.
        const typeAlias = tempSource.getTypeAlias('T')
        if (!typeAlias) {
            return undefined
        }

        // 3. The resolved type's properties are the table's field names.
        //    For 'change_request' this includes its own fields plus inherited
        //    fields from 'task', system columns, etc.
        const resolvedType = typeAlias.getType()
        const properties = resolvedType.getProperties()

        if (properties.length === 0) {
            return undefined
        }

        const fieldNames = new Set(properties.map((p) => p.getName()))
        tableFieldNamesCache.set(tableName, fieldNames)
        return fieldNames
    } finally {
        // 4. Always clean up — the temp file should never persist on disk or
        //    in the compiler's source file set.
        compiler.removeSourceFile(tempSource)
    }
}
